// Utility functions for managing chart view toggle state in localStorage
// Uses simple structure: portfolio:toggles with three boolean values

import { CacheKeys } from './cacheKeys';

export type ChartViewType = 'pct' | 'dollar' | 'dol1' | 'dol2';

export type ToggleState = {
  portfolio_toggle: boolean;  // true = $ view, false = % view (default)
  account_toggle: boolean;      // true = $ view for ALL accounts, false = % view (default)
  ticker_toggle: boolean;       // true = dol2 view for ALL tickers, false = dol1 view (default)
};

/**
 * Get the current toggle state from localStorage
 * Defaults to all false if not set
 */
export function getToggleState(): ToggleState {
  try {
    const key = CacheKeys.toggles.all();
    const raw = localStorage.getItem(key);
    if (!raw) {
      return { portfolio_toggle: false, account_toggle: false, ticker_toggle: false };
    }
    const parsed = JSON.parse(raw) as ToggleState;
    // Ensure all three values exist, defaulting to false if missing
    return {
      portfolio_toggle: parsed.portfolio_toggle ?? false,
      account_toggle: parsed.account_toggle ?? false,
      ticker_toggle: parsed.ticker_toggle ?? false,
    };
  } catch {
    return { portfolio_toggle: false, account_toggle: false, ticker_toggle: false };
  }
}

/**
 * Save the toggle state to localStorage
 */
function saveToggleState(state: ToggleState): void {
  try {
    const key = CacheKeys.toggles.all();
    const stateJson = JSON.stringify(state);
    localStorage.setItem(key, stateJson);
    // Dispatch custom event for same-tab listeners
    window.dispatchEvent(new Event('toggleStateChanged'));
  } catch (error) {
    console.error('Failed to save toggle state:', error);
  }
}

/**
 * Get portfolio view (default: 'pct')
 */
export function getPortfolioView(): ChartViewType {
  const state = getToggleState();
  return state.portfolio_toggle === true ? 'dollar' : 'pct';
}

/**
 * Set portfolio view
 * @param view - 'pct' (default) or 'dollar'
 */
export function setPortfolioView(view: ChartViewType): void {
  const state = getToggleState();
  state.portfolio_toggle = view === 'dollar';
  saveToggleState(state);
}

/**
 * Get account view (default: 'pct')
 * Note: account_toggle affects ALL accounts globally
 */
export function getAccountView(accountId: string): ChartViewType {
  const state = getToggleState();
  return state.account_toggle === true ? 'dollar' : 'pct';
}

/**
 * Set account view
 * Note: This sets the global account_toggle, affecting ALL accounts
 * @param accountId - Account ID (unused but kept for API compatibility)
 * @param view - 'pct' (default) or 'dollar'
 */
export function setAccountView(accountId: string, view: ChartViewType): void {
  const state = getToggleState();
  state.account_toggle = view === 'dollar';
  saveToggleState(state);
}

/**
 * Get ticker view (default: 'dol1')
 * Note: ticker_toggle affects ALL tickers globally
 */
export function getTickerView(accountId: string, ticker: string): ChartViewType {
  const state = getToggleState();
  return state.ticker_toggle === true ? 'dol2' : 'dol1';
}

/**
 * Set ticker view
 * Note: This sets the global ticker_toggle, affecting ALL tickers
 * @param accountId - Account ID (unused but kept for API compatibility)
 * @param ticker - Ticker symbol (unused but kept for API compatibility)
 * @param view - 'dol1' (default) or 'dol2'
 */
export function setTickerView(accountId: string, ticker: string, view: ChartViewType): void {
  const state = getToggleState();
  state.ticker_toggle = view === 'dol2';
  saveToggleState(state);
}

